/**
 * Signup Component
 * Handles user registration with invitation code
 * Invitation validation happens during registration (no separate verification step)
 */

import React, { useState } from "react";
// import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Container,
  Stepper,
  Step,
  StepLabel
} from "@mui/material";
import {
  initiateRegistration,
  authorizeRegistration,
  exchangeToken
} from "../services/api";

const Signup = ({ onSignupSuccess, onNavigateToLogin }) => {
  // const { executeRecaptcha } = useGoogleReCaptcha();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    email: "",
    code: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    otp: ""
  });

  const [registrationData, setRegistrationData] = useState({
    identifier: null,
    authorizationCode: null
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const steps = ["Enter Details", "Verify Email", "Complete"];

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.code) {
      newErrors.code = "Invitation code is required";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: Initiate registration
  const handleInitiateRegistration = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    let recaptchaToken = "";
    // Generate the reCAPTCHA token
    if (window.grecaptcha) {
      recaptchaToken = await window.grecaptcha.enterprise.execute(
        "6LdLSforAAAAAMrgvpCE4tIvO4tqQVToFtN6hsSm",
        { action: "signup" }
      );
    }

    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const result = await initiateRegistration(
        formData.email,
        formData.code,
        formData.password,
        recaptchaToken,
        formData.firstName,
        formData.lastName
      );

      if (result.success) {
        setMessage({
          type: "success",
          text: "Verification code sent to your email! Please check your inbox."
        });
        // Use the identifier returned by Salesforce, not the email
        const identifier = result.identifier || formData.email;
        setRegistrationData({
          ...registrationData,
          identifier: identifier
        });
        setActiveStep(1); // Move to OTP verification step
      } else {
        setMessage({
          type: "error",
          text: result.message || "Registration initiation failed"
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "An error occurred during registration"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP and authorize
  const handleAuthorize = async (e) => {
    e.preventDefault();

    if (!formData.otp) {
      setErrors({ otp: "Verification code is required" });
      return;
    }

    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const result = await authorizeRegistration(
        registrationData.identifier,
        formData.otp
      );
      const { data, success } = result || {},
        { code } = data || {};

      if (success) {
        setMessage({
          type: "success",
          text: "Email verified! Completing registration..."
        });
        setRegistrationData({
          ...registrationData,
          authorizationCode: code
        });

        // Automatically proceed to token exchange
        await handleTokenExchange(code);
      } else {
        setMessage({
          type: "error",
          text: result.message || "Verification failed"
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "An error occurred during verification"
      });
      setIsLoading(false);
    }
  };

  // Step 3: Exchange token
  const handleTokenExchange = async (authCode) => {
    try {
      const result = await exchangeToken(
        authCode || registrationData.authorizationCode
      );

      if (result.success) {
        setMessage({
          type: "success",
          text: "Registration complete! Redirecting to login..."
        });
        setActiveStep(2); // Move to complete step

        // Redirect to login after 2 seconds
        setTimeout(() => {
          if (onSignupSuccess) {
            onSignupSuccess(formData.email);
          }
        }, 2000);
      } else {
        setMessage({
          type: "error",
          text: result.message || "Token exchange failed"
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "An error occurred during token exchange"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4
        }}
      >
        <Card sx={{ width: "100%", boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" align="center" gutterBottom>
              Sign Up
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Enter your invitation code to create an account
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {message.text && (
              <Alert severity={message.type} sx={{ mb: 3 }}>
                {message.text}
              </Alert>
            )}

            {/* Step 1: Registration Form */}
            {activeStep === 0 && (
              <Box
                component="form"
                onSubmit={handleInitiateRegistration}
                noValidate
              >
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="Enter your email"
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Invitation Code"
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  error={!!errors.code}
                  helperText={errors.code}
                  placeholder="Enter your invitation code"
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="First Name"
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  placeholder="Enter your first name"
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  placeholder="Enter your last name"
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  placeholder="Enter your password (min 8 characters)"
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  placeholder="Confirm your password"
                  margin="normal"
                  required
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{ mt: 3 }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1 }}
                        color="inherit"
                      />
                      Sending Verification Code...
                    </>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </Box>
            )}

            {/* Step 2: OTP Verification */}
            {activeStep === 1 && (
              <Box component="form" onSubmit={handleAuthorize} noValidate>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  We've sent a verification code to {formData.email}. Please
                  enter it below.
                </Typography>

                <TextField
                  fullWidth
                  label="Verification Code"
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  error={!!errors.otp}
                  helperText={errors.otp}
                  placeholder="Enter the verification code"
                  margin="normal"
                  required
                  autoFocus
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{ mt: 3 }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1 }}
                        color="inherit"
                      />
                      Verifying...
                    </>
                  ) : (
                    "Verify Email"
                  )}
                </Button>
              </Box>
            )}

            {/* Step 3: Complete */}
            {activeStep === 2 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="success.main" gutterBottom>
                  Registration Complete!
                </Typography>
                <Typography variant="body1">
                  You will be redirected to the login page shortly...
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2">
                Already have an account?{" "}
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={onNavigateToLogin}
                  sx={{ cursor: "pointer" }}
                >
                  Login here
                </MuiLink>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Signup;
